import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { initials } from '@/lib/academic'

export const Skeleton: FC<{ className?: string }> = ({ className }): ReactNode => {
    return <div className={cn('animate-pulse rounded-md bg-muted', className)} aria-hidden='true' />
}

type EmptyStateProps = {
    icon?: ReactNode
    title: string
    description?: ReactNode
    action?: ReactNode
    className?: string
}

export const EmptyState: FC<EmptyStateProps> = ({ icon, title, description, action, className }): ReactNode => {
    return (
        <div className={cn('flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center', className)}>
            {icon && (
                <span className='mb-2 grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary ring-8 ring-primary/5 [&_svg]:size-6' aria-hidden='true'>
                    {icon}
                </span>
            )}

            <p className='text-[15px] font-semibold text-foreground'>
                {title}
            </p>

            {description && (
                <p className='max-w-sm text-[13px] leading-relaxed text-muted-foreground'>
                    {description}
                </p>
            )}

            {action && (
                <div className='mt-3 flex flex-wrap justify-center gap-2'>
                    {action}
                </div>
            )}
        </div>
    )
}

const AVATAR_TONES = [
    'bg-primary/12 text-primary',
    'bg-success/12 text-success',
    'bg-info/12 text-info',
    'bg-warning/12 text-warning'
]

const hashText = (value: string): number => {
    let hash = 0

    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) >>> 0
    }

    return hash
}

type AvatarProps = {
    name: string
    className?: string
}

export const Avatar: FC<AvatarProps> = ({ name, className }): ReactNode => {
    return (
        <span
            className={cn(
                'grid size-9 shrink-0 place-items-center rounded-full text-xs font-bold tracking-wide',
                AVATAR_TONES[hashText(name) % AVATAR_TONES.length],
                className
            )}
            aria-hidden='true'
        >
            {initials(name)}
        </span>
    )
}

type ProgressProps = {
    value: number
    tone?: 'primary' | 'success' | 'warning' | 'danger'
    className?: string
    label?: string
}

const PROGRESS_TONES = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-destructive'
}

export const Progress: FC<ProgressProps> = ({ value, tone = 'primary', className, label }): ReactNode => {
    const clamped = Math.max(0, Math.min(100, value))

    return (
        <div
            className={cn('h-2 w-full overflow-hidden rounded-full bg-muted', className)}
            role='progressbar'
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(clamped)}
        >
            <div className={cn('h-full rounded-full transition-[width] duration-500', PROGRESS_TONES[tone])} style={{ width: `${clamped}%` }} />
        </div>
    )
}
